import React, { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, Brain, Wind, Target, Flame, Music, Heart, Eye, Sparkles, Leaf, Clock, BookOpen, Lock } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  meditation: Brain,
  breathwork: Wind,
  chakra: Target,
  kundalini: Flame,
  mantras: Music,
  yoga: Heart,
  consciousness: Eye,
  ancient_secrets: Sparkles,
  mindfulness: Leaf,
};

const DIFFICULTY_COLORS = {
  beginner: "bg-[#8FA69A]/20 text-[#2C4A3B]",
  intermediate: "bg-[#D4AF37]/20 text-[#8B7520]",
  advanced: "bg-[#C17B5F]/20 text-[#8B4530]",
};

function PracticeCard({ practice }) {
  const Icon = CATEGORY_ICONS[practice.category] || Sparkles;
  return (
    <Link
      to={`/practice/${practice.id}`}
      data-testid={`practice-card-${practice.id}`}
      className="group relative p-6 rounded-2xl bg-white border border-[#E3DDD2] hover:shadow-md transition-all duration-300 hover:-translate-y-0.5"
    >
      <div className="flex items-center gap-2 mb-3">
        <Badge className={`${DIFFICULTY_COLORS[practice.difficulty]} border-0 rounded-full text-xs`}>
          {practice.difficulty}
        </Badge>
        {practice.is_secret && (
          <Badge className="bg-[#D4AF37]/20 text-[#8B7520] border-0 rounded-full text-xs flex items-center gap-1">
            <Lock className="w-3 h-3" strokeWidth={1.5} />
            Secret
          </Badge>
        )}
      </div>
      <h3 className="font-heading text-lg font-medium text-[#1A221E] leading-snug group-hover:text-[#2C4A3B] transition-colors">
        {practice.title}
      </h3>
      <p className="mt-2 text-sm text-[#737E79] leading-relaxed line-clamp-2">
        {practice.description}
      </p>
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-[#8FA69A]">
          <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span className="capitalize">{practice.tradition}</span>
        </div>
        <div className="flex items-center gap-1 text-xs text-[#737E79]">
          <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
          {practice.duration_minutes} min
        </div>
      </div>
    </Link>
  );
}

export default function LibraryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [practices, setPractices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [difficultyFilter, setDifficultyFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const activeCategory = searchParams.get("category") || "all";

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/practices`),
      axios.get(`${API}/categories`),
    ]).then(([pRes, cRes]) => {
      setPractices(pRes.data);
      setCategories(cRes.data);
      setLoading(false);
    }).catch((err) => {
      console.error(err);
      setLoading(false);
    });
  }, []);

  const filteredPractices = useMemo(() => {
    return practices.filter((p) => {
      const matchCat = activeCategory === "all" || p.category === activeCategory;
      const matchDiff = difficultyFilter === "all" || p.difficulty === difficultyFilter;
      const matchSearch =
        !searchQuery ||
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.tradition.toLowerCase().includes(searchQuery.toLowerCase());
      return matchCat && matchDiff && matchSearch;
    });
  }, [practices, activeCategory, difficultyFilter, searchQuery]);

  const setCategory = (cat) => {
    if (cat === "all") {
      searchParams.delete("category");
    } else {
      searchParams.set("category", cat);
    }
    setSearchParams(searchParams);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="library-loading">
        <div className="w-8 h-8 rounded-full border-2 border-[#8FA69A] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <main className="min-h-screen" data-testid="library-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-8 pb-16">
        {/* Header */}
        <div className="mb-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] mb-2">
            Sacred Knowledge
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light text-[#1A221E]">
            Practice Library
          </h1>
          <p className="mt-3 text-base text-[#737E79] max-w-xl leading-relaxed">
            {practices.length} practices across {categories.length} traditions, from beginner to advanced
          </p>
        </div>

        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737E79]" strokeWidth={1.5} />
            <Input
              data-testid="library-search-input"
              placeholder="Search practices, traditions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-full bg-white border-[#E3DDD2] focus:ring-[#8FA69A] focus:border-[#8FA69A] text-sm"
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="difficulty-filter-dropdown"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#E3DDD2] bg-white text-sm text-[#737E79] hover:bg-[#EAE2D6] transition-colors"
            >
              <BookOpen className="w-4 h-4" strokeWidth={1.5} />
              {difficultyFilter === "all" ? "All Levels" : difficultyFilter.charAt(0).toUpperCase() + difficultyFilter.slice(1)}
            </DropdownMenuTrigger>
            <DropdownMenuContent className="rounded-xl">
              {["all", "beginner", "intermediate", "advanced"].map((d) => (
                <DropdownMenuItem
                  key={d}
                  data-testid={`difficulty-option-${d}`}
                  onClick={() => setDifficultyFilter(d)}
                  className="cursor-pointer capitalize"
                >
                  {d === "all" ? "All Levels" : d}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Category Tabs */}
        <Tabs value={activeCategory} onValueChange={setCategory}>
          <ScrollArea className="w-full">
            <TabsList
              data-testid="category-tabs"
              className="h-auto bg-transparent p-0 gap-2 flex-wrap justify-start mb-8"
            >
              <TabsTrigger
                value="all"
                data-testid="tab-all"
                className="rounded-full px-4 py-2 text-sm data-[state=active]:bg-[#8FA69A] data-[state=active]:text-white data-[state=active]:shadow-none bg-white border border-[#E3DDD2] text-[#737E79] hover:bg-[#EAE2D6]"
              >
                All Practices
              </TabsTrigger>
              {categories.map((cat) => {
                const Icon = CATEGORY_ICONS[cat.id] || Sparkles;
                return (
                  <TabsTrigger
                    key={cat.id}
                    value={cat.id}
                    data-testid={`tab-${cat.id}`}
                    className="rounded-full px-4 py-2 text-sm data-[state=active]:bg-[#8FA69A] data-[state=active]:text-white data-[state=active]:shadow-none bg-white border border-[#E3DDD2] text-[#737E79] hover:bg-[#EAE2D6] flex items-center gap-1.5"
                  >
                    <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {cat.name}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </ScrollArea>

          <TabsContent value={activeCategory} className="mt-0">
            {filteredPractices.length === 0 ? (
              <div className="text-center py-20" data-testid="no-results">
                <p className="text-[#737E79]">No practices found. Try a different search or filter.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="practices-grid">
                {filteredPractices.map((practice) => (
                  <PracticeCard key={practice.id} practice={practice} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
